import { isEmpty, toString } from 'lodash-es';
import type { LogMsgRow } from './CallLogInformation.model';
import { CallLogInformationSeverity } from './CallLogInformation.model';

const severityRegex = /^(?<prefix>.*)(?<severity>INFO|SEVERE|ERROR|WARNING|WARN)(?<suffix>.*)/u;

export function doesMatchSeverityRegex(line: string): boolean {
  return severityRegex.test(line);
}

export interface RegexStructure {
  readonly prefix: string;
  readonly suffix: string;
  readonly severity: string;
}

export function splitSeverityRegex(line: string): RegexStructure {
  if (!severityRegex.test(line)) {
    return {
      prefix: '',
      suffix: '',
      severity: '',
    };
  }

  const executed = severityRegex.exec(line);
  if (isEmpty(executed?.groups)) {
    return {
      prefix: '',
      suffix: '',
      severity: '',
    };
  }
  return {
    prefix: executed?.groups?.prefix,
    suffix: executed?.groups?.suffix,
    severity: executed?.groups?.severity,
  };
}

export function getSeverity(lineSlice: string): CallLogInformationSeverity {
  switch (lineSlice) {
    case 'ERROR':
    case 'SEVERE': {
      return CallLogInformationSeverity.Error;
    }
    case 'WARN':
    case 'WARNING': {
      return CallLogInformationSeverity.Warning;
    }

    default: {
      return CallLogInformationSeverity.Info;
    }
  }
}

export function getLineWithSeverityAttributes(line: string): LogMsgRow {
  const { prefix, suffix, severity } = splitSeverityRegex(line);
  return {
    prefix,
    severity: getSeverity(severity),
    message: `${severity}${suffix}`,
  };
}

export function getLineAttributes(line: string): LogMsgRow {
  return doesMatchSeverityRegex(line) ? getLineWithSeverityAttributes(line) : { message: line };
}

export function parseLogError(shouldHighlightSeverity?: boolean): (line: string) => LogMsgRow {
  return (line: string) => {
    if (!shouldHighlightSeverity) {
      return { message: line };
    }
    const { severity, prefix, message } = getLineAttributes(line);
    return severity
      ? {
          severity,
          prefix,
          message,
        }
      : {
          message,
        };
  };
}

export function exportTxtFile(fileContent: string, fileName: string) {
  const url = URL.createObjectURL(new Blob([fileContent], { type: 'text/plain' }));

  const linkElement = document.createElement('a');
  linkElement.href = url;
  linkElement.download = fileName;
  document.body.appendChild(linkElement);
  linkElement.click();

  document.body.removeChild(linkElement);
  URL.revokeObjectURL(url);
}

export function exportCallLogs(logLines?: string[], callId?: string) {
  return () => {
    const fileContent = logLines ? logLines?.join('\n') : '';
    const fileName = `call-log-${toString(callId)}.txt`;
    exportTxtFile(fileContent, fileName);
  };
}
